import type { AiReviewSettingsErrorCode } from '@/lib/ai-review-settings/errors';
import type {
  AutoApproveAllowedOpenIssues,
  AutoApproveBehavior,
  AutoApproveMode,
  AutoFixLoopMode,
  AutoFixProvider,
  AutoUltrareviewMode,
  UltrareviewMode,
  VerifyMode,
} from '@/lib/ai-review-settings/schema';
import type {
  AutoApproveExcludePatterns,
  AutoApproveFilters,
  AutoApproveIncludePatterns,
  AutoFixLoopFilters,
  IgnorePatterns,
  IncludePatterns,
  SensitivityLevel,
  VerifyFilters,
} from '@/lib/ai-review-settings/types';
import { toast } from 'sonner';

import { parseAIReviewSettingsApiError } from './api-error';

import { AI_REVIEW_SETTINGS_ERROR_CODE } from '@/lib/ai-review-settings/errors';
import { AIReviewSettingsUpdateSchema } from '@/lib/ai-review-settings/types';

/**
 * Picks only defined fields from an object.
 * This keeps our PATCH payloads free of `undefined`, matching what
 * the server-side validator expects.
 */
export function includeDefinedFields<T extends Record<string, any>>(
  source: T,
  fields: ReadonlyArray<keyof T>,
): Partial<T> {
  const result: Partial<T> = {};

  for (const field of fields) {
    if (source[field] !== undefined) {
      result[field] = source[field];
    }
  }

  return result;
}

/**
 * Shape of the settings we accept from the client.
 * We can’t reuse Prisma’s type here because JSON columns resolve to `unknown`,
 * so we keep a minimal representation that maps 1:1 with what the API accepts.
 *
 * Both mutation hooks share this input; single-repo sends it with ids, bulk omits ids.
 */
export type AIReviewSettingsInput = {
  generalReviewEnabled?: boolean;
  customInstructions?: string | null;
  documentationLinks?: Array<{
    id: string;
    libraryName: string;
    version: string;
    url: string;
  }> | null;
  ignorePatterns?: IgnorePatterns;
  includePatterns?: IncludePatterns;
  autoApproveExcludePatterns?: AutoApproveExcludePatterns;
  autoApproveIncludePatterns?: AutoApproveIncludePatterns;
  autoApproveFilters?: AutoApproveFilters;
  ignoreBranchPatterns?: IgnorePatterns;
  ignoreBaseBranchPatterns?: IgnorePatterns;
  ignoreLabels?: Array<{ id: string; label: string }> | null;
  ignoreTitlePatterns?: IgnorePatterns;
  checkDrafts?: boolean;
  generatePrDescriptionEnabled?: boolean;
  prDescriptionReviewLinkEnabled?: boolean;
  skipPrDescriptionIfAuthorProvided?: boolean;
  sensitivity?: SensitivityLevel;
  autoApproveBehavior?: AutoApproveBehavior;
  autoApproveMode?: AutoApproveMode;
  autoApproveCustomPrompt?: string | null;
  autoApproveExcludeExternalContributors?: boolean;
  autoApproveAllowedOpenIssues?: AutoApproveAllowedOpenIssues;
  ultrareviewMode?: UltrareviewMode;
  autoUltrareviewMode?: AutoUltrareviewMode;
  autoUltrareviewCustomPrompt?: string | null;
  autoUltrareviewFilePatterns?: IgnorePatterns;
  mergeConfidenceEnabled?: boolean;
  summaryInstructions?: string | null;
  autoFixButtonEnabled?: boolean;
  autoFixProvider?: AutoFixProvider;
  prCommentFixesEnabled?: boolean;
  fixCommitsToPr?: boolean;
  autoFixLoopMode?: AutoFixLoopMode;
  autoFixLoopFilters?: AutoFixLoopFilters;
  verifyMode?: VerifyMode;
  verifyFilters?: VerifyFilters;
  verifyInstructions?: string | null;
  codingAgentInstructions?: string | null;
  maxChangedLines?: number | null;
  incrementalReviewsEnabled?: boolean;
  architectureDiagramsEnabled?: boolean;
  externalContributorsRequireManualReview?: boolean;
  resolveThreadsWhenAddressed?: boolean;
};

// Central list of updatable fields for compile-time safety.
// Adding a new mutable field requires updating this list, otherwise the
// `__assert*` aliases below will fail to compile.
export const UPDATABLE_FIELDS = [
  'generalReviewEnabled',
  'customInstructions',
  'summaryInstructions',
  'documentationLinks',
  'ignorePatterns',
  'includePatterns',
  'autoApproveExcludePatterns',
  'autoApproveIncludePatterns',
  'autoApproveFilters',
  'ignoreBranchPatterns',
  'ignoreBaseBranchPatterns',
  'ignoreLabels',
  'ignoreTitlePatterns',
  'checkDrafts',
  'generatePrDescriptionEnabled',
  'prDescriptionReviewLinkEnabled',
  'skipPrDescriptionIfAuthorProvided',
  'sensitivity',
  'autoApproveBehavior',
  'autoApproveMode',
  'autoApproveCustomPrompt',
  'autoApproveExcludeExternalContributors',
  'autoApproveAllowedOpenIssues',
  'ultrareviewMode',
  'autoUltrareviewMode',
  'autoUltrareviewCustomPrompt',
  'autoUltrareviewFilePatterns',
  'mergeConfidenceEnabled',
  'autoFixButtonEnabled',
  'autoFixProvider',
  'prCommentFixesEnabled',
  'fixCommitsToPr',
  'autoFixLoopMode',
  'autoFixLoopFilters',
  'verifyMode',
  'verifyFilters',
  'verifyInstructions',
  'codingAgentInstructions',
  'maxChangedLines',
  'incrementalReviewsEnabled',
  'architectureDiagramsEnabled',
  'externalContributorsRequireManualReview',
  'resolveThreadsWhenAddressed',
] as const satisfies readonly (keyof AIReviewSettingsInput)[];

// Assert at compile time that we have no missing or extra fields
type NonIdKeys = Exclude<keyof AIReviewSettingsInput, 'installationId' | 'repositoryId'>;
type MissingUpdatableKeys = Exclude<NonIdKeys, (typeof UPDATABLE_FIELDS)[number]>;
type ExtraUpdatableKeys = Exclude<(typeof UPDATABLE_FIELDS)[number], NonIdKeys>;
// These type aliases will error at compile-time if the lists diverge
export type __assertNoMissingUpdatableKeys = MissingUpdatableKeys extends never ? true : never;
export type __assertNoExtraUpdatableKeys = ExtraUpdatableKeys extends never ? true : never;

export const DEFAULT_UPDATE_ERROR = 'Failed to update settings';

// Shown when a write is rejected for lacking permission (e.g. viewer role).
// Title states the cause; description explains who can make the change.
const PERMISSION_DENIED_UPDATE_TITLE = "You don't have permission to change settings";
const PERMISSION_DENIED_UPDATE_DESCRIPTION = 'Ask an admin to give you a member or admin role.';
const REPOSITORY_WORKSPACE_MISMATCH_UPDATE_TITLE =
  'This repository belongs to a different workspace';
const REPOSITORY_WORKSPACE_MISMATCH_UPDATE_DESCRIPTION =
  "Switch to the repository's workspace, refresh the page, and try again.";

// Stable analytics discriminator for failures. Decoupled from user-facing copy
// so reworded toasts never fragment `ai_review_settings_update_failed` reporting.
export const SETTINGS_UPDATE_ERROR_CODE = {
  permissionDenied: 'permission_denied',
  repositoryWorkspaceMismatch: 'repository_workspace_mismatch',
  requestFailed: 'request_failed',
  unknown: 'unknown',
} as const;

export type SettingsUpdateErrorCode =
  (typeof SETTINGS_UPDATE_ERROR_CODE)[keyof typeof SETTINGS_UPDATE_ERROR_CODE];

/**
 * Settings-update failure carrying everything the toast and analytics need.
 * `code` is the stable discriminator; `title`/`description` are user-facing copy.
 * Keeping all three on the error means copy changes can never silently drop the
 * description or change what we report to analytics.
 */
export class SettingsUpdateError extends Error {
  constructor(
    readonly code: SettingsUpdateErrorCode,
    readonly title: string,
    readonly description?: string,
  ) {
    super(title);
    this.name = 'SettingsUpdateError';
  }
}

/**
 * Builds the failure for a non-OK response. Known workspace mismatches and
 * permission failures get actionable copy; everything else uses the generic error.
 */
function settingsUpdateErrorForResponse(
  status: number,
  serverErrorCode: AiReviewSettingsErrorCode | undefined,
): SettingsUpdateError {
  if (serverErrorCode === AI_REVIEW_SETTINGS_ERROR_CODE.repositoryInstallationMismatch) {
    return new SettingsUpdateError(
      SETTINGS_UPDATE_ERROR_CODE.repositoryWorkspaceMismatch,
      REPOSITORY_WORKSPACE_MISMATCH_UPDATE_TITLE,
      REPOSITORY_WORKSPACE_MISMATCH_UPDATE_DESCRIPTION,
    );
  }

  if (status === 403) {
    return new SettingsUpdateError(
      SETTINGS_UPDATE_ERROR_CODE.permissionDenied,
      PERMISSION_DENIED_UPDATE_TITLE,
      PERMISSION_DENIED_UPDATE_DESCRIPTION,
    );
  }

  return new SettingsUpdateError(SETTINGS_UPDATE_ERROR_CODE.requestFailed, DEFAULT_UPDATE_ERROR);
}

/** Stable analytics code for any failure, defaulting to `unknown` for untyped errors. */
export function settingsUpdateErrorCode(err: unknown): SettingsUpdateErrorCode {
  return err instanceof SettingsUpdateError ? err.code : SETTINGS_UPDATE_ERROR_CODE.unknown;
}

/**
 * Throws a typed error for a failed settings response, logging the raw server
 * message first. Both current structured errors and legacy string errors are
 * parsed through the shared settings API error boundary.
 */
export function throwOnSettingsUpdateError(
  response: Response,
  payload: unknown,
  logPrefix: string,
): void {
  if (response.ok) {
    return;
  }

  const { code: serverErrorCode, message: serverMessage } = parseAIReviewSettingsApiError(payload);

  if (typeof serverMessage === 'string' && serverMessage) {
    console.error(`${logPrefix} Update failed:`, serverMessage);
  }

  throw settingsUpdateErrorForResponse(response.status, serverErrorCode);
}

/** Renders a settings-update failure, preserving the typed title/description. */
export function showSettingsUpdateErrorToast(err: unknown, toastId?: string | number): void {
  const title = err instanceof SettingsUpdateError ? err.title : DEFAULT_UPDATE_ERROR;
  const description = err instanceof SettingsUpdateError ? err.description : undefined;

  toast.error(title, { id: toastId, description });
}

export function buildUpdateRequestBody(
  installationId: number,
  repositoryId: number,
  newSettings: AIReviewSettingsInput,
) {
  // Only forward fields the user actually touched. This mirrors the server-side
  // merge logic and avoids clobbering existing JSON blobs with `undefined`.
  const partial = includeDefinedFields(newSettings, UPDATABLE_FIELDS);

  // Re-validate client payloads with the exact same Zod schema the API uses.
  return AIReviewSettingsUpdateSchema.parse({
    installationId,
    repositoryId,
    ...partial,
  });
}

export function buildInstallationSettingsUpdateRequestBody(newSettings: AIReviewSettingsInput) {
  const partial = includeDefinedFields(newSettings, UPDATABLE_FIELDS);

  if (Object.keys(partial).length === 0) {
    throw new Error('No settings provided to update');
  }

  return partial;
}
